import { useState, useEffect, useMemo } from 'react';
import type { Listing } from '../types';
import { listListings, archiveListing, restoreListing, duplicateListing } from '../api/client';
import { colors } from '../constants/colors';
import { PaginationControls } from './PaginationControls';
import { Img } from './Img';

const PAGE_SIZE = 20;
const GRID_COLUMNS = '56px 2fr 1fr 1fr 120px';

interface InventorySectionProps {
  onAddListing: () => void;
  onEditListing: (listing: Listing) => void;
  refreshTrigger?: number;
}

export function InventorySection({ onAddListing, onEditListing, refreshTrigger }: InventorySectionProps) {
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [showArchived] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(listings.length / PAGE_SIZE);
  const paginatedListings = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return listings.slice(start, start + PAGE_SIZE);
  }, [listings, currentPage]);

  const loadListings = async () => {
    setLoading(true);
    try {
      const data = await listListings(showArchived);
      setListings(data);
    } catch (err) {
      console.error('Failed to load listings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadListings();
  }, [showArchived, refreshTrigger]);

  const handleArchive = async (listing: Listing) => {
    setActionLoading(listing.id);
    try {
      if (listing.isArchived) {
        await restoreListing(listing.id);
      } else {
        await archiveListing(listing.id);
      }
      await loadListings();
    } catch (err) {
      console.error('Failed to archive/restore listing:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDuplicate = async (listing: Listing) => {
    setActionLoading(listing.id);
    try {
      await duplicateListing(listing.id);
      await loadListings();
    } catch (err) {
      console.error('Failed to duplicate listing:', err);
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <section>
      <div style={styles.header}>
        <h2 style={styles.sectionTitle}>Inventory</h2>
        <div style={styles.headerActions}>
          <button onClick={onAddListing} style={styles.addButton}>
            + Add Listing
          </button>
        </div>
      </div>

      {loading ? (
        <div style={styles.loadingState}>Loading inventory...</div>
      ) : listings.length === 0 ? (
        <div style={styles.emptyState}>
          <PackageIcon />
          <p>No listings yet</p>
          <button onClick={onAddListing} style={styles.emptyAddButton}>
            Add your first listing
          </button>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <div style={{ ...styles.tableHeader, gridTemplateColumns: GRID_COLUMNS }}>
            <span></span>
            <span>Name</span>
            <span>Price</span>
            <span>Stock</span>
            <span style={{ textAlign: 'right' }}>Quick Actions</span>
          </div>
          {paginatedListings.map((listing) => (
            <div
              key={listing.id}
              style={{
                ...styles.tableRow,
                gridTemplateColumns: GRID_COLUMNS,
                ...(listing.isArchived ? styles.archivedRow : {}),
              }}
            >
              <div>
                <div style={styles.imageWrapper}>
                  {listing.imageUrl ? (
                    <Img src={listing.imageUrl} alt={listing.name} variant="thumb" renderedWidth={40} blurhash={listing.blurhash} style={styles.listingImage} />
                  ) : (
                    <div style={styles.listingImagePlaceholder}>
                      <PackageIcon size={16} />
                    </div>
                  )}
                  {listing.isArchived && (
                    <span style={styles.archivedBadge}>Archived</span>
                  )}
                </div>
              </div>
              <div style={styles.cell}>
                <span style={styles.listingName}>{listing.name}</span>
                {listing.description && (
                  <span style={styles.listingDescription}>{listing.description}</span>
                )}
              </div>
              <div>
                <span style={styles.listingPrice}>${(listing.startingPrice ?? 0).toFixed(2)}</span>
              </div>
              <div>
                <span style={styles.listingStock}>
                  {listing.isUnique ? 'Unique' : listing.stock}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={styles.actionsGroup}>
                  <button
                    onClick={() => onEditListing(listing)}
                    style={styles.actionBtn}
                    disabled={actionLoading === listing.id}
                    title="Edit"
                  >
                    <EditIcon />
                  </button>
                  <button
                    onClick={() => handleDuplicate(listing)}
                    style={styles.actionBtn}
                    disabled={actionLoading === listing.id}
                    title="Duplicate"
                  >
                    <CopyIcon />
                  </button>
                  <button
                    onClick={() => handleArchive(listing)}
                    style={styles.actionBtn}
                    disabled={actionLoading === listing.id}
                    title={listing.isArchived ? 'Restore' : 'Archive'}
                  >
                    {listing.isArchived ? <RestoreIcon /> : <ArchiveIcon />}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <PaginationControls
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </section>
  );
}

function PackageIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={colors.gray[600]} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21" />
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
      <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
      <line x1="12" y1="22.08" x2="12" y2="12" />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

function ArchiveIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function RestoreIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="1 4 1 10 7 10" />
      <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
    </svg>
  );
}

const styles: Record<string, React.CSSProperties> = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 700,
    margin: 0,
    color: colors.ink.black,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  addButton: {
    padding: '8px 16px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  loadingState: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: colors.gray[600],
  },
  emptyAddButton: {
    marginTop: '12px',
    padding: '10px 20px',
    backgroundColor: colors.brand.blue,
    color: colors.white,
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  tableContainer: {
    backgroundColor: colors.white,
    borderRadius: '16px',
    border: `1px solid ${colors.gray[300]}`,
    overflow: 'hidden',
  },
  tableHeader: {
    display: 'grid',
    gap: '16px',
    padding: '12px 16px',
    backgroundColor: colors.gray[100],
    borderRadius: '8px 8px 0 0',
    fontSize: '12px',
    fontWeight: 600,
    color: colors.gray[600],
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tableRow: {
    display: 'grid',
    gap: '16px',
    padding: '14px 16px',
    borderBottom: `1px solid ${colors.gray[200]}`,
    alignItems: 'center',
    transition: 'background-color 0.15s',
  },
  archivedRow: {
    opacity: 0.5,
    backgroundColor: colors.gray[100],
  },
  cell: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
  },
  imageWrapper: {
    position: 'relative',
  },
  listingImage: {
    width: '40px',
    height: '40px',
    objectFit: 'cover',
    borderRadius: '6px',
  },
  listingImagePlaceholder: {
    width: '40px',
    height: '40px',
    backgroundColor: colors.gray[200],
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  archivedBadge: {
    position: 'absolute',
    top: '-6px',
    right: '-6px',
    backgroundColor: colors.gray[600],
    color: colors.white,
    fontSize: '8px',
    fontWeight: 600,
    padding: '2px 4px',
    borderRadius: '3px',
  },
  listingName: {
    fontSize: '14px',
    fontWeight: 600,
    color: colors.ink.black,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  listingDescription: {
    fontSize: '12px',
    color: colors.gray[600],
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginTop: '2px',
  },
  listingPrice: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#22C55E',
  },
  listingStock: {
    fontSize: '14px',
    color: colors.ink.black,
  },
  actionsGroup: {
    display: 'flex',
    gap: '6px',
  },
  actionBtn: {
    width: '32px',
    height: '32px',
    backgroundColor: colors.white,
    border: `1px solid ${colors.gray[300]}`,
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.gray[600],
  },
};
